/**
 * dreambooth.app/birthday — the first hardcoded micro app on the /new chassis.
 *
 * A themed, opinionated skin over the same machinery: pick one of six curated
 * birthday art directions, say whose birthday it is, sign in with Google, and
 * the regular onboarding pipeline builds the booth. After generation this page
 * hands off to the /new result editor (`/new?step=look`) — the store is shared,
 * so the visitor lands in the full editor with their themed booth loaded.
 *
 * Look: scrapbook editorial (see DESIGN.md in this folder) — cream paper, ink,
 * one cherry-red accent, rotated polaroid theme cards, washi-tape accents.
 * Deliberately nothing like /new's dark studio shell.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaArrowLeft, FaCamera, FaGoogle, FaSpinner, FaTimes } from 'react-icons/fa'
import { useOnboardingStore } from '../new/onboardingStore'
import { generateBooth, fetchStudioSession, ApiError, TimeoutError } from '../new/onboardingApi'
import type { StudioSessionUser } from '../new/onboardingApi'
import { signInWithStudio } from '../new/studioAuth'
import { clearStudioAuth } from '../new/studioToken'
import { generationLocale, useLang } from '../new/regionCopy'
import { BIRTHDAY_THEMES } from './themes'

const FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,700&display=swap'

/** Paper-grain overlay as an inline SVG — no asset, no extra request. */
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E\")"

const INK = '#221B12'
const PAPER = '#F7F1E5'
const CHERRY = '#C8322B'
const BUTTER = '#F2C14E'
const SKY = '#88A9D2'

const COPY = {
  en: {
    docTitle: 'Birthday photobooth — dreambooth.app/birthday',
    kicker: 'a dreambooth mini app',
    titleA: 'A photobooth for',
    titleB: 'their birthday',
    sub: 'Pick a vibe, tell us the name, and a full matching booth — welcome screen, photo strips, filters — builds itself in about a minute.',
    pickTheme: 'pick the vibe',
    whose: "Whose birthday?",
    namePh: 'Their name (or yours!)',
    agePh: 'Age (optional)',
    extraPh: 'Anything else? "add our cat", "green theme"… (optional)',
    photosLabel: 'Their photos',
    photosSub: 'Add 1–3 photos — they go INTO the welcome design, like a scrapbook page made for them.',
    addPhoto: 'Add photo',
    needPhoto: 'Add at least one photo of the birthday person — that’s what makes it theirs.',
    skipPhotos: 'Continue without photos',
    photoTooBig: 'Photos must be under 2MB.',
    photoNotImage: 'That file isn’t an image.',
    build: 'Build the booth',
    buildSignedOut: 'Sign in & build the booth',
    building: 'Building your booth…',
    buildingSub: 'A full design takes 2–4 minutes. Keep this page open.',
    buildingSlow: 'Detailed designs (especially with photos) take the longest — almost there.',
    stageLabels: [
      'Reading the brief',
      'Designing the welcome screen',
      'Drawing the laptop version',
      'Mixing colors & backgrounds',
      'Finishing touches'
    ],
    needName: 'Tell us the birthday person’s name first.',
    signInFirst: 'Sign in with Google first — two clicks, then your booth builds.',
    failed: 'That didn’t work. Try again.',
    timedOut: 'It took too long. Try again — your theme and name are kept.',
    back: 'Back to themes',
    footer: 'Booths are free to make. Built on dreambooth.'
  },
  id: {
    docTitle: 'Photobooth ulang tahun — dreambooth.app/birthday',
    kicker: 'mini app dari dreambooth',
    titleA: 'Photobooth untuk',
    titleB: 'ulang tahunnya',
    sub: 'Pilih vibe-nya, tulis namanya — booth lengkap dengan layar sambutan, strip foto, dan filter langsung jadi dalam semenit.',
    pickTheme: 'pilih vibe-nya',
    whose: 'Ulang tahun siapa?',
    namePh: 'Namanya (atau namamu!)',
    agePh: 'Umur (opsional)',
    extraPh: 'Ada tambahan? "pakai kucing kami", "tema hijau"… (opsional)',
    photosLabel: 'Foto dia',
    photosSub: 'Tambah 1–3 foto — fotonya masuk KE desain layar sambutan, kayak halaman scrapbook yang dibikin khusus.',
    addPhoto: 'Tambah foto',
    needPhoto: 'Tambah minimal satu foto yang ulang tahun ya — itu yang bikin booth-nya "dia banget".',
    skipPhotos: 'Lanjut tanpa foto',
    photoTooBig: 'Foto harus di bawah 2MB.',
    photoNotImage: 'File itu bukan gambar.',
    build: 'Buatkan booth-nya',
    buildSignedOut: 'Login & buatkan booth-nya',
    building: 'Booth-mu lagi dibuat…',
    buildingSub: 'Desain lengkap butuh 2–4 menit. Jangan tutup halaman ini ya.',
    buildingSlow: 'Desain yang detail (apalagi pakai foto) paling lama — dikit lagi jadi.',
    stageLabels: [
      'Membaca brief-nya',
      'Mendesain layar sambutan',
      'Menggambar versi laptop',
      'Meracik warna & latar',
      'Sentuhan terakhir'
    ],
    needName: 'Isi dulu nama yang ulang tahun ya.',
    signInFirst: 'Login dengan Google dulu — dua klik, lalu booth kamu langsung dibuat.',
    failed: 'Gagal nih. Coba lagi ya.',
    timedOut: 'Kelamaan nih. Coba lagi — tema dan namanya masih tersimpan.',
    back: 'Balik ke tema',
    footer: 'Bikin booth gratis. Dibangun di atas dreambooth.'
  }
} as const

const CARD_TILT = [-3, 2, -2, 3, -1.5, 2.5]

const Birthday: React.FC = () => {
  const navigate = useNavigate()
  const lang = useLang()
  const c = COPY[lang]
  const [themeId, setThemeId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [age, setAge] = useState('')
  const [extra, setExtra] = useState('')
  const [photos, setPhotos] = useState<{ dataUrl: string; name: string }[]>([])
  const [skipPhotos, setSkipPhotos] = useState(false)
  const [busy, setBusy] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [sessionUser, setSessionUser] = useState<StudioSessionUser | null>(null)
  const theme = useMemo(
    () => BIRTHDAY_THEMES.find((t) => t.id === themeId) ?? null,
    [themeId]
  )

  // This route's fonts (Instrument Serif + DM Sans), loaded only here.
  useEffect(() => {
    if (document.querySelector(`link[href="${FONTS_HREF}"]`)) return
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = FONTS_HREF
    document.head.appendChild(link)
  }, [])

  useEffect(() => {
    const prev = document.title
    document.title = c.docTitle
    return () => {
      document.title = prev
    }
  }, [c.docTitle])

  useEffect(() => {
    let cancelled = false
    fetchStudioSession().then((user) => {
      if (!cancelled) setSessionUser(user)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Honest progress while the pipeline runs. Thresholds mirror the measured
  // pipeline (spec ~10s, welcome design 35–110s, landscape 70–180s, then
  // backgrounds + finishing) — a lying "±1 menit" spinner read as a hang and
  // made real 3-minute runs look failed.
  const STAGE_AT = [0, 15, 110, 190, 240]
  useEffect(() => {
    if (!busy) {
      setElapsed(0)
      return
    }
    const t = setInterval(() => setElapsed((e) => e + 1), 1000)
    return () => clearInterval(t)
  }, [busy])
  const stageIndex = STAGE_AT.filter((s) => elapsed >= s).length - 1

  const ensureSignIn = async (): Promise<boolean> => {
    if (sessionUser?.email) return true
    const user = await signInWithStudio()
    if (user) {
      setSessionUser(user)
      return true
    }
    return false
  }

  const MAX_PHOTO_BYTES = 2 * 1024 * 1024

  const handlePhotoFiles = (files: FileList | null): void => {
    if (!files) return
    setError(null)
    for (const file of Array.from(files).slice(0, 3 - photos.length)) {
      if (!file.type.startsWith('image/')) {
        setError(c.photoNotImage)
        continue
      }
      if (file.size > MAX_PHOTO_BYTES) {
        setError(c.photoTooBig)
        continue
      }
      const reader = new FileReader()
      reader.onload = () =>
        setPhotos((prev) =>
          prev.length >= 3 ? prev : [...prev, { dataUrl: String(reader.result), name: file.name }]
        )
      reader.readAsDataURL(file)
    }
  }

  const handleBuild = async (): Promise<void> => {
    if (!theme || busy) return
    const trimmed = name.trim()
    if (trimmed.length < 2) {
      setError(c.needName)
      return
    }
    // The photos ARE the product — the karelazee bar: the birthday person
    // baked into the welcome design. Required unless explicitly skipped.
    if (photos.length === 0 && !skipPhotos) {
      setError(c.needPhoto)
      return
    }
    setError(null)

    // Same decision as /new: the account comes first, then the model runs.
    const ok = await ensureSignIn()
    if (!ok) {
      setError(c.signInFirst)
      return
    }

    const prompt = theme.prompt(
      { name: trimmed, age: age.trim().slice(0, 3), extra: extra.trim().slice(0, 120) },
      lang
    )
    const st = useOnboardingStore.getState()
    st.startOver() // a birthday build is always a fresh draft, never a rebuild
    st.setPrompt(prompt)
    st.startGenerating()
    setBusy(true)
    try {
      // The theme's curated artwork rides along as a STYLE reference — the
      // frame-gen approach: the designer model gets a template that is
      // already exactly our taste instead of improvising from adjectives.
      // The server accepts at most 3 attachments; the visitor's own photos
      // always win that budget, so at 3 photos the words carry the theme.
      const attachments: { dataUrl: string; name?: string; role: 'literal' | 'style' }[] =
        photos.map((p) => ({ dataUrl: p.dataUrl, name: p.name, role: 'literal' as const }))
      if (theme.styleRef && photos.length < 3) {
        try {
          const blob = await (await fetch(theme.styleRef)).blob()
          const refDataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(String(reader.result))
            reader.onerror = () => reject(reader.error)
            reader.readAsDataURL(blob)
          })
          attachments.unshift({ dataUrl: refDataUrl, name: `${theme.id}-template`, role: 'style' })
        } catch {
          /* template ref is an enhancement — never block the build on it */
        }
      }
      const res = await generateBooth({
        prompt,
        locale: generationLocale(),
        attachments
      })
      const store = useOnboardingStore.getState()
      store.applyGeneration(res)
      store.setStepIndex(0)
      // Hand off to the /new editor — shared store, themed booth loaded.
      navigate('/new?step=look')
    } catch (err) {
      setBusy(false)
      if (err instanceof ApiError && err.status === 401) clearStudioAuth()
      useOnboardingStore.getState().generationFailed('')
      setError(
        err instanceof TimeoutError
          ? c.timedOut
          : err instanceof ApiError && err.status === 401
            ? c.signInFirst
            : err instanceof Error && err.message
              ? err.message
              : c.failed
      )
    }
  }

  const serif = "'Instrument Serif', Georgia, serif"
  const sans = "'DM Sans', system-ui, sans-serif"

  return (
    <div
      className="min-h-screen w-full overflow-x-hidden"
      style={{ background: PAPER, backgroundImage: GRAIN, color: INK, fontFamily: sans }}
    >
      {/* Navbar */}
      <header className="mx-auto flex w-full max-w-[1060px] items-center justify-between px-6 pt-6">
        <a href="/new" className="text-sm font-medium tracking-tight" style={{ color: INK }}>
          dreambooth<span style={{ color: CHERRY }}>/birthday</span>
        </a>
        <span
          className="rounded-full border px-3 py-1 text-[11px]"
          style={{ borderColor: `${INK}33`, color: `${INK}99` }}
        >
          {c.kicker}
        </span>
      </header>

      {busy ? (
        /* ---- Building screen ---- */
        <main className="mx-auto flex w-full max-w-[560px] flex-col items-center px-6 pb-24 pt-24 text-center">
          <div
            className="mb-8 flex h-40 w-32 items-center justify-center rounded-[14px] border-2"
            style={{
              borderColor: INK,
              background: theme?.card.bg ?? '#fff',
              transform: 'rotate(-3deg)',
              boxShadow: `4px 5px 0 ${INK}1f`
            }}
          >
            <FaSpinner
              className="animate-spin text-2xl"
              style={{ color: theme?.dark ? BUTTER : CHERRY }}
            />
          </div>
          <h1 className="mb-2 text-4xl" style={{ fontFamily: serif }}>
            {c.building}
          </h1>
          <p className="text-sm" style={{ color: `${INK}99` }}>
            {c.buildingSub}
          </p>
          <ul className="mt-7 flex flex-col items-start gap-2 text-left">
            {c.stageLabels.map((label, i) => (
              <li key={label} className="flex items-center gap-2.5 text-[13.5px]">
                {i < stageIndex ? (
                  <span style={{ color: CHERRY }}>✓</span>
                ) : i === stageIndex ? (
                  <FaSpinner className="animate-spin text-[11px]" style={{ color: CHERRY }} />
                ) : (
                  <span style={{ color: `${INK}44` }}>·</span>
                )}
                <span style={{ color: i <= stageIndex ? INK : `${INK}66` }}>{label}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 font-mono text-[13px] tabular-nums" style={{ color: `${INK}88` }}>
            {String(Math.floor(elapsed / 60))}:{String(elapsed % 60).padStart(2, '0')}
          </p>
          {elapsed >= 150 && (
            <p className="mt-2 max-w-sm text-[12.5px]" style={{ color: `${INK}99` }}>
              {c.buildingSlow}
            </p>
          )}
        </main>
      ) : (
        <main className="mx-auto w-full max-w-[1060px] px-6 pb-20">
          {/* Hero */}
          <section className="pb-10 pt-14 text-center">
            <h1 className="mx-auto max-w-3xl text-5xl leading-[1.05] sm:text-6xl" style={{ fontFamily: serif }}>
              {c.titleA}{' '}
              <em
                className="relative inline-block px-1 not-italic"
                style={{ fontStyle: 'italic', color: CHERRY }}
              >
                {c.titleB}
                <svg
                  viewBox="0 0 200 12"
                  className="absolute -bottom-2 left-0 w-full"
                  preserveAspectRatio="none"
                  aria-hidden
                >
                  <path
                    d="M4 8 C 50 2, 150 12, 196 5"
                    fill="none"
                    stroke={CHERRY}
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </em>
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed" style={{ color: `${INK}b0` }}>
              {c.sub}
            </p>
          </section>

          {/* Theme picker */}
          <section>
            <div className="mb-5 flex items-center gap-3">
              <span
                className="inline-block rounded-sm px-2 py-0.5 text-[11px] font-semibold uppercase tracking-widest"
                style={{ background: BUTTER, transform: 'rotate(-1.5deg)' }}
              >
                {c.pickTheme}
              </span>
              <span className="h-px flex-1" style={{ background: `${INK}22` }} />
            </div>

            <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
              {BIRTHDAY_THEMES.map((t, i) => {
                const active = t.id === themeId
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      setThemeId(t.id)
                      setError(null)
                    }}
                    className="group rounded-[14px] border-2 p-3 text-left transition-transform duration-200 ease-out"
                    style={{
                      borderColor: active ? CHERRY : INK,
                      background: t.card.bg,
                      color: t.card.ink,
                      transform: active ? 'rotate(0deg) scale(1.03)' : `rotate(${CARD_TILT[i % CARD_TILT.length]}deg)`,
                      boxShadow: active ? `5px 6px 0 ${CHERRY}40` : `3px 4px 0 ${INK}1f`
                    }}
                  >
                    {/* washi tape */}
                    <span
                      className="mx-auto -mt-6 mb-2 block h-4 w-16 rounded-[2px] opacity-80"
                      style={{ background: t.card.tape, transform: 'rotate(-4deg)' }}
                    />
                    {t.card.thumb ? (
                      // A real welcome screen built with this theme — the card
                      // sells the outcome, not a monogram.
                      <img
                        src={t.card.thumb}
                        alt={t.title}
                        loading="lazy"
                        draggable={false}
                        className="aspect-[12/5] w-full rounded-[8px] object-cover"
                      />
                    ) : (
                      <span
                        className="flex h-24 items-center justify-center rounded-[8px] text-6xl sm:h-28"
                        style={{
                          fontFamily: serif,
                          fontStyle: 'italic',
                          color: t.card.accent,
                          background: t.dark ? '#00000026' : '#ffffff66'
                        }}
                      >
                        {t.card.glyph}
                      </span>
                    )}
                    <span className="mt-3 block text-[15px] font-bold leading-tight">{t.title}</span>
                    <span className="mt-0.5 block text-[12px] leading-snug opacity-70">
                      {t.tagline[lang]}
                    </span>
                  </button>
                )
              })}
            </div>
          </section>

          {/* Details + CTA — appears once a theme is picked */}
          {theme && (
            <section
              className="mx-auto mt-10 max-w-xl rounded-[14px] border-2 p-6"
              style={{ borderColor: INK, background: '#FFFdf6', boxShadow: `4px 5px 0 ${INK}1f` }}
            >
              <h2 className="text-2xl" style={{ fontFamily: serif }}>
                {c.whose}
              </h2>
              <div className="mt-4 flex flex-col gap-3">
                <div className="flex gap-3">
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={c.namePh}
                    maxLength={40}
                    className="min-w-0 flex-1 rounded-[10px] border-2 bg-white px-4 py-2.5 text-[15px] outline-none"
                    style={{ borderColor: `${INK}44`, color: INK }}
                  />
                  <input
                    value={age}
                    onChange={(e) => setAge(e.target.value.replace(/\D/g, '').slice(0, 3))}
                    placeholder={c.agePh}
                    inputMode="numeric"
                    className="w-28 rounded-[10px] border-2 bg-white px-4 py-2.5 text-[15px] outline-none"
                    style={{ borderColor: `${INK}44`, color: INK }}
                  />
                </div>
                <input
                  value={extra}
                  onChange={(e) => setExtra(e.target.value)}
                  placeholder={c.extraPh}
                  maxLength={120}
                  className="rounded-[10px] border-2 bg-white px-4 py-2.5 text-[15px] outline-none"
                  style={{ borderColor: `${INK}44`, color: INK }}
                />
              </div>

              {/* Photos — the karelazee bar: the birthday person goes INTO the design */}
              <div className="mt-5">
                <div className="flex items-baseline gap-2">
                  <span className="text-[15px] font-bold" style={{ color: INK }}>
                    {c.photosLabel}
                  </span>
                  <span
                    className="rounded-sm px-1.5 text-[10px] font-semibold uppercase tracking-wider"
                    style={{ background: BUTTER, transform: 'rotate(-1deg)' }}
                  >
                    1–3
                  </span>
                </div>
                <p className="mt-1 text-[12.5px] leading-snug" style={{ color: `${INK}99` }}>
                  {c.photosSub}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    handlePhotoFiles(e.target.files)
                    e.target.value = ''
                  }}
                />
                <div className="mt-3 flex flex-wrap items-start gap-3">
                  {photos.map((p, i) => (
                    <div
                      key={`${p.name}-${i}`}
                      className="relative h-24 w-20 rounded-[6px] border-2 bg-white p-1.5 pb-4"
                      style={{
                        borderColor: INK,
                        transform: `rotate(${i % 2 === 0 ? -2.5 : 2}deg)`,
                        boxShadow: `2px 3px 0 ${INK}1f`
                      }}
                    >
                      <img
                        src={p.dataUrl}
                        alt={p.name}
                        className="h-full w-full rounded-[3px] object-cover"
                      />
                      <button
                        type="button"
                        aria-label="remove photo"
                        onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}
                        className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full text-[9px] text-white"
                        style={{ background: CHERRY }}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  {photos.length < 3 && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex h-24 w-20 flex-col items-center justify-center gap-1.5 rounded-[6px] border-2 border-dashed text-[11px] font-medium transition-colors"
                      style={{
                        borderColor: `${INK}55`,
                        color: `${INK}88`,
                        transform: 'rotate(1.5deg)'
                      }}
                    >
                      <FaCamera className="text-base" style={{ color: CHERRY }} />
                      {c.addPhoto}
                    </button>
                  )}
                </div>
                {photos.length === 0 && (
                  <button
                    type="button"
                    onClick={() => setSkipPhotos((v) => !v)}
                    className="mt-2 text-[11.5px] underline-offset-2 hover:underline"
                    style={{ color: skipPhotos ? CHERRY : `${INK}77` }}
                  >
                    {skipPhotos ? '✓ ' : ''}
                    {c.skipPhotos}
                  </button>
                )}
              </div>

              {error && (
                <p className="mt-3 text-[13px] font-medium" style={{ color: CHERRY }}>
                  {error}
                </p>
              )}

              <div className="mt-5 flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={() => setThemeId(null)}
                  className="inline-flex items-center gap-2 text-[13px] underline-offset-2 hover:underline"
                  style={{ color: `${INK}99` }}
                >
                  <FaArrowLeft className="text-[10px]" /> {c.back}
                </button>
                <button
                  type="button"
                  onClick={() => void handleBuild()}
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-[15px] font-bold text-white transition-transform duration-200 hover:scale-[1.03]"
                  style={{ background: CHERRY, boxShadow: `3px 4px 0 ${INK}33` }}
                >
                  {sessionUser?.email ? (
                    c.build
                  ) : (
                    <>
                      <FaGoogle /> {c.buildSignedOut}
                    </>
                  )}
                </button>
              </div>
            </section>
          )}

          <footer className="mt-16 text-center text-[12px]" style={{ color: `${INK}77` }}>
            {c.footer} <span style={{ color: SKY }}>●</span>{' '}
            <a href="/new" className="underline underline-offset-2">
              dreambooth.app/new
            </a>
          </footer>
        </main>
      )}
    </div>
  )
}

export default Birthday
